import { fabric } from 'fabric';

import { CanvasHandler } from '.';
import { WorkareaLayout, WorkareaObject, FabricImage } from "../types/FabricCustomType";

import { VideoObject } from '../canvas/fabric/Video';

class WorkareaHandler {
	canvasHandler: CanvasHandler;

	constructor(canvasHandler: CanvasHandler) {
		this.canvasHandler = canvasHandler;
		this.initialize();
	}

	/**
	 * Initialize workarea
	 *
	 * @author salgum1114
	 */
	public initialize() {
		const { workareaOption } = this.canvasHandler;
		const image = new Image(workareaOption.width, workareaOption.height);
		image.width = workareaOption.width;
		image.height = workareaOption.height;
		this.canvasHandler.workarea = new fabric.Image(image, workareaOption) as WorkareaObject;
		// this.canvasHandler.canvas.add(this.canvasHandler.workarea);
		this.canvasHandler.objects = this.canvasHandler.getObjects();
		this.canvasHandler.canvas.centerObject(this.canvasHandler.workarea);
		this.canvasHandler.canvas.renderAll();
	}

	/**
	 * Set the layout on workarea
	 * @param {WorkareaLayout} layout
	 * @returns
	 */
	public setLayout = (layout: WorkareaLayout) => {
		this.canvasHandler.workarea.set('layout', layout);
		const { _element, isElement, workareaWidth, workareaHeight } = this.canvasHandler.workarea;
		const { canvas } = this.canvasHandler;
		let scaleX = 1;
		let scaleY = 1;
		const isFixed = layout === 'fixed';
		const isResponsive = layout === 'responsive';
		const isFullscreen = layout === 'fullscreen';
		if (isElement) {
			if (isFixed) {
				scaleX = workareaWidth / _element.width;
				scaleY = workareaHeight / _element.height;
			} else if (isResponsive) {
				const scales = this.calculateScale();
				scaleX = scales.scaleX;
				scaleY = scales.scaleY;
			} else {
				scaleX = canvas.getWidth() / _element.width;
				scaleY = canvas.getHeight() / _element.height;
			}
		}
		this.canvasHandler.getObjects().forEach(obj => {
			const { id, player } = obj as VideoObject;
			if (id !== 'workarea') {
				const objScaleX = !isFullscreen ? 1 : scaleX;
				const objScaleY = !isFullscreen ? 1 : scaleY;
				const objWidth = obj.width * objScaleX * canvas.getZoom();
				const objHeight = obj.height * objScaleY * canvas.getZoom();
				const el = this.canvasHandler.elementHandler.findById(obj.id);
				this.canvasHandler.elementHandler.setSize(el, obj);
				if (player) {
					player.setPlayerSize(objWidth, objHeight);
				}
				obj.set({
					scaleX: !isFullscreen ? 1 : objScaleX,
					scaleY: !isFullscreen ? 1 : objScaleY,
				});
			}
		});
		if (isResponsive) {
			const center = canvas.getCenter();
			if (isElement) {
				this.canvasHandler.workarea.set({
					scaleX: 1,
					scaleY: 1,
				});
				this.canvasHandler.zoomHandler.zoomToPoint(new fabric.Point(center.left, center.top), scaleX);
			} else {
				this.canvasHandler.workarea.set({
					width: workareaWidth,
					height: workareaHeight,
				});
				scaleX = canvas.getWidth() / workareaWidth;
				scaleY = canvas.getHeight() / workareaHeight;
				if (workareaHeight >= workareaWidth) {
					scaleX = scaleY;
				} else {
					scaleY = scaleX;
				}
				this.canvasHandler.zoomHandler.zoomToPoint(new fabric.Point(center.left, center.top), scaleX);
			}
			canvas.centerObject(this.canvasHandler.workarea);
			canvas.renderAll();
			return;
		}
		if (isElement) {
			this.canvasHandler.workarea.set({
				width: _element.width,
				height: _element.height,
				scaleX,
				scaleY,
			});
		} else {
			const width = isFixed ? workareaWidth : this.canvasHandler.canvas.getWidth();
			const height = isFixed ? workareaHeight : this.canvasHandler.canvas.getHeight();
			this.canvasHandler.workarea.set({
				width,
				height,
				backgroundColor: 'rgba(255, 255, 255, 1)',
			});
			this.canvasHandler.canvas.renderAll();
			if (isFixed) {
				canvas.centerObject(this.canvasHandler.workarea);
			} else {
				this.canvasHandler.workarea.set({
					left: 0,
					top: 0,
				});
			}
		}
		canvas.centerObject(this.canvasHandler.workarea);
		const center = canvas.getCenter();
		this.canvasHandler.zoomHandler.zoomToPoint(new fabric.Point(center.left, center.top), 1);
		canvas.renderAll();
	};

	/**
	 * Set the responsive image on Workarea
	 * @param {string | File} [source]
	 * @param {boolean} [loaded]
	 * @returns
	 */
	public setResponsiveImage = async (source: string | File, loaded?: boolean) => {
		const imageFromUrl = async (src: string = '') => {
			return new Promise<WorkareaObject>(resolve => {
				fabric.Image.fromURL(src, (img: any) => {
					const { canvas, workarea, editable } = this.canvasHandler;
					const { workareaWidth, workareaHeight } = workarea;
					const { scaleX, scaleY } = this.calculateScale(img);
					if (img._element) {
						workarea.set({
							...img,
							isElement: true,
							selectable: false,
						});
					} else {
						const image = new Image(workareaWidth, workareaHeight);
						workarea.setElement(image);
						workarea.set({
							isElement: false,
							selectable: false,
							width: workareaWidth,
							height: workareaHeight,
						});
					}
					if (editable && !loaded) {
						canvas.getObjects().forEach(obj => {
							const { id, player } = obj as VideoObject;
							if (id !== 'workarea') {
								const objWidth = obj.width * scaleX;
								const objHeight = obj.height * scaleY;
								const el = this.canvasHandler.elementHandler.findById(id);
								this.canvasHandler.elementHandler.setScaleOrAngle(el, obj);
								this.canvasHandler.elementHandler.setSize(el, obj);
								if (player) {
									player.setPlayerSize(objWidth, objHeight);
								}
								obj.set({
									scaleX: 1,
									scaleY: 1,
								});
								obj.setCoords();
							}
						});
					}
					this.canvasHandler.zoomHandler.zoomToFit();
					canvas.centerObject(workarea);
					resolve(workarea);
				});
			});
		};
		const { workarea } = this.canvasHandler;
		if (!source) {
			workarea.set({
				src: null,
				file: null,
			});
			return imageFromUrl(source as string);
		}
		if (source instanceof File) {
			return new Promise<WorkareaObject>(resolve => {
				const reader = new FileReader();
				reader.onload = () => {
					workarea.set({
						file: source,
					});
					imageFromUrl(reader.result as string).then(resolve);
				};
				reader.readAsDataURL(source);
			});
		} else {
			workarea.set({
				src: source,
			});
			return imageFromUrl(source);
		}
	};

	/**
	 * Set the image on Workarea
	 * @param {string | File} source
	 * @param {boolean} [loaded=false]
	 * @returns
	 */
	setImage = async (source: string | File, loaded = false) => {
		let objects = this.canvasHandler.canvas.getObjects("image");
		if (objects.length == 0) {
			// this.canvasHandler.workareaHandler.initialize();
			this.canvasHandler.canvas.add(this.canvasHandler.workarea);
			this.canvasHandler.objects = this.canvasHandler.getObjects();
			this.canvasHandler.canvas.centerObject(this.canvasHandler.workarea);
			this.canvasHandler.canvas.renderAll();
			objects = this.canvasHandler.canvas.getObjects("image");
			this.canvasHandler.canvas.sendToBack(objects[0])
		}
		const { canvas, workarea, editable } = this.canvasHandler;
		if (workarea.layout === 'responsive') {
			return this.setResponsiveImage(source, loaded);
		}
		const imageFromUrl = async (src: string) => {
			return new Promise<WorkareaObject>(resolve => {
				fabric.Image.fromURL(src, (img: any) => {
					let width = canvas.getWidth();
					let height = canvas.getHeight();
					if (workarea.layout === 'fixed') {
						width = workarea.width * workarea.scaleX;
						height = workarea.height * workarea.scaleY;
					}
					let scaleX = 1;
					let scaleY = 1;
					if (img._element) {
						// scaleX = width / img.width;
						// scaleY = height / img.height;}
						img.set({
							originX: 'left',
							originY: 'top',
							scaleX,
							scaleY,
							opacity: 0.5
						});
						workarea.set({
							...img,
							isElement: true,
							selectable: false,
						});
					} else {
						workarea.setElement(new Image());
						workarea.set({
							width,
							height,
							scaleX,
							scaleY,
							isElement: false,
							selectable: false,
						});
					}
					canvas.centerObject(workarea);
					if (editable && !loaded) {
						const { layout } = workarea;
						canvas.getObjects().forEach(obj => {
							const { id, player } = obj as VideoObject;
							if (id !== 'workarea') {
								scaleX = layout === 'fullscreen' ? scaleX : obj.scaleX;
								scaleY = layout === 'fullscreen' ? scaleY : obj.scaleY;
								const el = this.canvasHandler.elementHandler.findById(id);
								this.canvasHandler.elementHandler.setSize(el, obj);
								if (player) {
									const objWidth = obj.width * scaleX;
									const objHeight = obj.height * scaleY;
									player.setPlayerSize(objWidth, objHeight);
								}
								obj.set({
									scaleX,
									scaleY,
								});
								obj.setCoords();
							}
						});
					}
					const center = canvas.getCenter();
					const zoom = loaded || workarea.layout === 'fullscreen' ? 1 : this.canvasHandler.canvas.getZoom();
					this.canvasHandler.zoomHandler.zoomToPoint(new fabric.Point(center.left, center.top), zoom);
					canvas.renderAll();
					resolve(workarea);
				});
			});
		};
		if (!source) {
			objects.forEach(work => {
				this.canvasHandler.canvas.remove(work);
			});
		}
		if (source instanceof File) {
			return new Promise<WorkareaObject>(resolve => {
				const reader = new FileReader();
				reader.onload = () => {
					workarea.set({
						file: source,
					});
					imageFromUrl(reader.result as string).then(resolve);
				};
				reader.readAsDataURL(source);
			});
		} else {
			workarea.set({
				src: source,
			});
			return imageFromUrl(source);
		}
	};

	/**
	 * Calculate scale to the image
	 *
	 * @param {FabricImage} [image]
	 * @returns
	 */
	public calculateScale = (image?: FabricImage) => {
		const { canvas, workarea } = this.canvasHandler;
		const { workareaWidth, workareaHeight } = workarea;
		const { _element } = image || workarea;
		const width = _element?.width || workareaWidth;
		const height = _element?.height || workareaHeight;
		let scaleX = canvas.getWidth() / width;
		let scaleY = canvas.getHeight() / height;
		if (height >= width) {
			scaleX = scaleY;
			if (canvas.getWidth() < width * scaleX) {
				scaleX = scaleX * (canvas.getWidth() / (width * scaleX));
			}
		} else {
			scaleY = scaleX;
			if (canvas.getHeight() < height * scaleX) {
				scaleX = scaleX * (canvas.getHeight() / (height * scaleX));
			}
		}
		return { scaleX, scaleY };
	};
}

export default WorkareaHandler;
